import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    OnInit,
    Signal,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { DepositFolderListBloc } from '@connect-services/client/blocs/deposit-folder-list.bloc';
import { AbstractPaginationComponent } from '@connect-services/client/components/abstract-pagination/abstract-pagination.component';
import { ManagementListHeaderComponent } from '@connect-services/client/components/management/management-list-header/management-list-header.component';
import { ManagementMainPanelTitleComponent } from '@connect-services/client/components/management/management-main-panel-title/management-main-panel-title.component';
import {
    CreateEditDataModalComponent,
    CreateEditDataModalForm,
} from '@connect-services/client/components/modals/create-edit-data-modal/create-edit-data-modal.component';
import { ExportDepositModalComponent } from '@connect-services/client/components/modals/deposit/export-deposit-modal/export-deposit-modal.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { OrganizationStorageUsageComponent } from '@connect-services/client/components/organization-storage-usage/organization-storage-usage.component';
import { IOPayload, NotificationEventEnum } from '@connect-services/client/models/notification.model';
import { DepositListComponent } from '@connect-services/client/pages/management/data/deposit/deposit-list/deposit-list.component';
import { DepositRowItemComponent } from '@connect-services/client/pages/management/data/deposit/deposit-row-item/deposit-row-item.component';
import { ManagementNoPermissionComponent } from '@connect-services/client/pages/management/no-permission/management-no-permission.component';
import { DepositExportData, ExportService, ExportStatusEnum } from '@connect-services/client/services/export.service';
import { ManagementDataService } from '@connect-services/client/services/management-data.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { WarehouseAndFormsAccessPermissionsService } from '@connect-services/client/services/warehouse-and-forms-access-permissions.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { ExportStatus, OrganizationsService } from '@connect-services/src/sdk/api';
import DepositStatusEnum from '@connect-services/src/sdk/async/DepositStatusEnum';
import ValidateDeposit from '@connect-services/src/sdk/async/ValidateDeposit';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum DataViewType {
    ORGANIZATION_DATA = 'organization-data',
    REFERENCE_DATA = 'reference-data',
}

export interface OrganizationStorageInformation {
    maxStorageInGo: number;
    usedStorageBytes: number;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CustomTableComponent,
        DepositListComponent,
        DepositRowItemComponent,
        ManagementListHeaderComponent,
        ManagementMainPanelTitleComponent,
        ManagementNoPermissionComponent,
        NoDataToDisplayComponent,
        OrganizationStorageUsageComponent,
        PaginatorComponent,
        RessourcesModule,
    ],
    selector: 'app-deposit',
    styleUrl: './deposit.component.scss',
    templateUrl: './deposit.component.html',
})
export class DepositComponent extends AbstractPaginationComponent implements OnInit {
    protected readonly ButtonType = ButtonType;
    protected readonly DataViewType = DataViewType;
    protected readonly ExportStatusEnum = ExportStatusEnum;
    protected readonly hasWarehousePermission = computed<boolean>(() =>
        this.warehouseAndFormsAccessPermissionsService.canAccessWarehouse(),
    );
    protected readonly organizationStorage = signal<OrganizationStorageInformation | null>(null);
    protected readonly title = computed<string>(() => {
        return this.viewType() === DataViewType.ORGANIZATION_DATA ? 'warehouse' : 'referenceData';
    });
    protected readonly viewType = computed<DataViewType>(() => {
        return this.routeData()['viewType'] ?? DataViewType.ORGANIZATION_DATA;
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly DepositFolderListBloc = inject(DepositFolderListBloc);
    protected readonly exportService = inject(ExportService);
    protected readonly managementDataService = inject(ManagementDataService);
    protected securityService = inject(SecurityService);
    protected readonly translateService = inject(TranslateService);

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly paginator: Signal<PaginatorComponent | undefined> = viewChild<PaginatorComponent>('paginator');
    private readonly routeData = toSignal(this.activatedRoute.data, {
        initialValue: this.activatedRoute.snapshot.data,
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly matDialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService<ValidateDeposit>);
    private readonly organizationService = inject(OrganizationsService);
    private readonly warehouseAndFormsAccessPermissionsService = inject(WarehouseAndFormsAccessPermissionsService);

    public constructor() {
        super();

        effect(() => {
            if (this.paginator()) {
                this.managementDataService.paginator = this.paginator();
            }
        });

        effect(() => {
            if (!this.DepositFolderListBloc.value()!.items) {
                this.managementDataService.isEmptyWarehouse.set(true);
            }

            this.managementDataService.displayTable();
        });

        effect(() => {
            this.managementDataService.currentView.set(this.viewType());
        });
        this.destroyRef.onDestroy(() => this.destroyCallback());
    }

    public override destroyCallback(): void {
        this.notificationService.off(
            NotificationEventEnum.DEPOSIT_VALIDATION,
            this.managementDataService.refreshDepositFolderList,
        );
        this.notificationService.off(
            NotificationEventEnum.DEPOSIT_EXPORT,
            this.managementDataService.refreshDepositFolderList,
        );
    }

    public async ngOnInit(): Promise<void> {
        await this.updateDepositExportStatus();
        await this.loadOrganizationStorage();

        this.notificationService.register(
            NotificationEventEnum.DEPOSIT_VALIDATION,
            (): string => {
                return 'depositValidationSuccess';
            },
            (data: IOPayload): string => {
                const depositStatus: DepositStatusEnum = (data as ValidateDeposit).depositStatus;

                return [DepositStatusEnum.DONE, DepositStatusEnum.ERRORED].includes(depositStatus)
                    ? 'depositValidationFailed'
                    : 'depositImportFailed';
            },
        );

        this.notificationService.register(
            NotificationEventEnum.DEPOSIT_EXPORT,
            (): string => {
                return 'depositExportSuccess';
            },
            (): string => {
                return 'depositExportFailed';
            },
        );

        this.notificationService.on(
            NotificationEventEnum.DEPOSIT_VALIDATION,
            this.managementDataService.refreshDepositFolderList,
        );

        this.notificationService.on(NotificationEventEnum.DEPOSIT_EXPORT, async () => {
            await this.updateDepositExportStatus();
        });
    }

    protected openAddDataModal(): void {
        const dialogRef = this.matDialog.open(CreateEditDataModalComponent, {
            autoFocus: false,
            data: { isEditMode: false, title: 'addData' },
            width: '50vw',
        });

        dialogRef.afterClosed().subscribe(async (response: CreateEditDataModalForm | null) => {
            if (!response?.value.name) {
                return;
            }

            await this.managementDataService.createDepositFolder(response);
        });
    }

    protected openExportDepositModal(): void {
        if (!this.hasWarehousePermission()) {
            return;
        }

        const dialogRef = this.matDialog.open(ExportDepositModalComponent, {
            autoFocus: false,
            width: '58vw',
        });

        dialogRef.afterClosed().subscribe(async (depositExportData: DepositExportData[] | null): Promise<void> => {
            if (!depositExportData) {
                return;
            }

            await this.exportService.exportDeposits(depositExportData);
            this.exportService.depositExportStatus.set(ExportStatusEnum.IN_PROGRESS);
        });
    }

    private async loadOrganizationStorage(): Promise<void> {
        if (this.viewType() !== DataViewType.ORGANIZATION_DATA) {
            return;
        }

        const organizationId: string | undefined = this.securityService.meSignal()?.organization.id;
        if (!organizationId) {
            return;
        }

        const { maxStorageInGo, usedStorageBytes } = await firstValueFrom(
            this.organizationService.organizationControllerGetStorage({ id: organizationId }),
        );

        if (maxStorageInGo == null || usedStorageBytes == null) {
            return;
        }

        this.organizationStorage.set({
            maxStorageInGo,
            usedStorageBytes,
        });
    }

    private async updateDepositExportStatus(): Promise<void> {
        if (!this.hasWarehousePermission()) {
            return;
        }

        const result = await this.exportService.getExportDepositStatus();

        this.exportService.depositExportStatus.set(
            result.status === ExportStatus.StatusEnum.RUNNING ? ExportStatusEnum.IN_PROGRESS : ExportStatusEnum.PENDING,
        );
    }
}
